// src/pages/MainPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../components/Header";
import NewFormulation from "../components/NewFormulation";
import SavedPage from "./SavedPage";
import { listFormulations, createFormulation, deleteFormulation } from "../data/store";
import { listBigFormulations } from "../data/bigStore";
import { auth, logout } from "../data/auth";
import { useRole } from "../data/useRole";
import "./MainPage.css";
import { db } from "../data/firebase";
import { collection, onSnapshot } from "firebase/firestore";

function uuid() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function getPagination(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const buttons = [];
  buttons.push(1);
  if (current > 4) buttons.push("...");
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); ++i) {
    if (i !== 1 && i !== total) buttons.push(i);
  }
  if (current < total - 3) buttons.push("...");
  buttons.push(total);
  return buttons;
}

// Normalize legacy records
function fixLegacyRecord(x, idx = 0) {
  const name = x.name || "";
  const code = x.code || "";
  const info = Array.isArray(x.info) ? x.info : [];
  const id = x.id || code || `legacy-${idx}`;
  return { ...x, id, name, code, info, legacy: true };
}

// ✅ Shared key normalizer
function getCardKey(rec) {
  return (
    (rec.raw || rec.color || (rec.name + (rec.info ? JSON.stringify(rec.info) : "")) || "")
      .trim()
      .toUpperCase()
  );
}

export default function MainPage() {
  const { role, loading } = useRole();
  const navigate = useNavigate();
  const location = useLocation();
  const view = location.pathname === "/search" ? "search" : "add";

  const PAGE_SIZE = 20;

  const [items, setItems] = useState([]); // modern
  const [legacyItems, setLegacyItems] = useState([]); // legacy
  const [legacyError, setLegacyError] = useState(null);
  const [query, setQuery] = useState("");
  const [toast, setToast] = useState(null);
  const [loadingItems, setLoadingItems] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [blacklistSet, setBlacklistSet] = useState(new Set());

  // ✅ Live Firestore listener for global blacklist
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "legacyblacklist-collection"), (snapshot) => {
      const set = new Set();
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (typeof data.color === "string") {
          set.add(data.color.trim().toUpperCase());
        }
      });
      setBlacklistSet(set);
      console.log("🔁 Live blacklist updated:", set);
    });
    return () => unsubscribe();
  }, []);

  // Route guard
  useEffect(() => {
    if (!loading && view === "add" && role !== "editor") {
      navigate("/search", { replace: true });
    }
  }, [loading, role, view, navigate]);

  // Load both modern + legacy lists
  useEffect(() => {
    let mounted = true;
    setLoadingItems(true);
    setLegacyError(null);

    async function loadAll() {
      try {
        const [modern, legacyRaw] = await Promise.all([
          listFormulations(),
          listBigFormulations(),
        ]);
        if (!mounted) return;

        setItems(Array.isArray(modern) ? modern : []);
        let fixedLegacy = [];

        if (Array.isArray(legacyRaw) && legacyRaw.length > 0) {
          fixedLegacy = legacyRaw.map((x, idx) => fixLegacyRecord(x, idx));
          fixedLegacy = fixedLegacy.filter((rec) => !blacklistSet.has(getCardKey(rec)));
        } else if (Array.isArray(legacyRaw) && legacyRaw.length === 0) {
          setLegacyError("No legacy formulas found (Firestore document is empty or missing).");
        } else {
          setLegacyError("Legacy database could not be loaded (not an array).");
        }

        setLegacyItems(fixedLegacy);
      } catch (err) {
        setLegacyError("Failed to load legacy formulas: " + (err?.message || err));
        setLegacyItems([]);
      } finally {
        if (mounted) setLoadingItems(false);
      }
    }

    if (view === "search") loadAll();
    else {
      setItems([]);
      setLegacyItems([]);
      setLoadingItems(false);
      setLegacyError(null);
    }

    return () => {
      mounted = false;
    };
  }, [view, blacklistSet]);

  async function handleSave(draft) {
    try {
      const { name, code, product, base, sizeL, notes, confidence, baseType, chart, rows } = draft;
      const item = {
        id: uuid(),
        name,
        code,
        product,
        base,
        sizeL,
        notes,
        confidence,
        baseType,
        chart: chart || "",
        rows,
        createdAt: new Date(),
        creatorEmail: auth.currentUser?.email || "",
      };
      await createFormulation(item);
      setItems((prev) => [item, ...prev]);
      setCurrentPage(1);
      showToast({ title: "Saved", msg: `“${item.name || "(Untitled)"}” saved`, type: "success" });
    } catch (err) {
      showToast({ title: "Not saved", msg: err.toString(), type: "error" });
    }
  }

  function handleDelete(id) {
    deleteFormulation(id)
      .then(() => {
        setItems((prev) => prev.filter((i) => i.id !== id));
        showToast({ title: "Deleted", msg: "Formulation removed", type: "info" });
      })
      .catch((err) => showToast({ title: "Error", msg: err.toString(), type: "error" }));
  }

  function showToast({ title, msg, type = "info", duration = 2000 }) {
    setToast({ title, msg, type });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), duration);
  }

  const filteredAll = useMemo(() => {
    if (view !== "search") return items;
    let all = [...items, ...legacyItems];
    if (!query) return all;
    const q = query.toLowerCase();
    return all.filter(
      (it) =>
        (it.name && it.name.toLowerCase().includes(q)) ||
        (it.code && it.code.toLowerCase().includes(q)) ||
        (Array.isArray(it.info) && it.info.join(" ").toLowerCase().includes(q)) ||
        (it.base && it.base.toLowerCase().includes(q)) ||
        (it.notes && it.notes.toLowerCase().includes(q)) ||
        JSON.stringify(it.rows || []).toLowerCase().includes(q)
    );
  }, [items, legacyItems, query, view]);

  // ✅ Pagination reset fix
  useEffect(() => {
    const totalPagesNow = Math.max(1, Math.ceil(filteredAll.length / PAGE_SIZE));
    if (currentPage > totalPagesNow) {
      setCurrentPage(1);
    }
  }, [filteredAll, blacklistSet, PAGE_SIZE, currentPage]);

  const totalPages = Math.max(1, Math.ceil(filteredAll.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageItems = filteredAll.slice(pageStart, pageStart + PAGE_SIZE);
  const pageNumbers = getPagination(currentPage, totalPages);

  if (loading || (view === "add" && role !== "editor")) return null;

  const PaginationBar = ({ position }) => (
    totalPages > 1 && (
      <div
        className={`pagination pagination--${position}`}
        role="navigation"
        aria-label={`Pagination ${position}`}
        style={{ marginBottom: position === "top" ? "10px" : "0", marginTop: position === "bottom" ? "10px" : "0" }}
      >
        <button
          className="page-btn"
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
        >
          Prev
        </button>
        {pageNumbers.map((n, idx) =>
          n === "..." ? (
            <span key={`${position}-dots${idx}`} className="page-btn page-btn--dots">
              ...
            </span>
          ) : (
            <button
              key={`${position}-${n}`}
              className={`page-btn${n === currentPage ? " page-btn--active" : ""}`}
              onClick={() => setCurrentPage(n)}
            >
              {n}
            </button>
          )
        )}
        <button
          className="page-btn"
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    )
  );

  return (
    <div className="app">
      <Header
        title="CCM Tinting Studio"
        showSearch={view === "search"}
        query={query}
        onQueryChange={setQuery}
        onSignOut={() => {
          logout().catch(() => {});
          navigate("/login", { replace: true });
        }}
      />

      <main className="container">
        {loadingItems ? (
          <div>Loading…</div>
        ) : (
          <>
            {view === "search" ? (
              <>
                <PaginationBar position="top" />

                <div style={{ marginBottom: "0.5rem", color: "#888", fontSize: "1rem" }}>
                  Showing {filteredAll.length} formulas
                  {legacyItems.length > 0 && (
                    <>
                      {" "}
                      — including <b>{legacyItems.length}</b> legacy formula
                      {legacyItems.length !== 1 && "s"}
                    </>
                  )}
                  {legacyError && (
                    <span style={{ color: "#c00", marginLeft: 10 }}>{legacyError}</span>
                  )}
                </div>

                <SavedPage
                  items={pageItems}
                  onDelete={handleDelete}
                  showLegacyBadge
                  blacklistSet={blacklistSet}
                />

                <PaginationBar position="bottom" />
              </>
            ) : (
              <NewFormulation onSave={handleSave} onError={showToast} />
            )}
          </>
        )}
      </main>

      {toast && (
        <div className={`toast toast--${toast.type}`}>
          <div className="toast__title">{toast.title}</div>
          <div className="toast__msg">{toast.msg}</div>
        </div>
      )}
    </div>
  );
}
